import { InfoCard } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import {
  SLACK_WORKSPACE_BASE_URL,
  TEAM_METADATA_ANNOTATIONS,
} from '@internal/team-metadata-common';
import {
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';

type TeamMetadataRow = {
  label: string;
  value: string;
  href?: string;
};

const getAnnotation = (entity: Entity, key: string): string | undefined =>
  entity.metadata.annotations?.[key];

export const buildTeamMetadataRows = (entity: Entity): TeamMetadataRow[] => {
  const rows: TeamMetadataRow[] = [];

  const teamTopic = getAnnotation(entity, TEAM_METADATA_ANNOTATIONS.teamTopic);
  if (teamTopic) {
    rows.push({ label: 'GitHub team topic', value: teamTopic });
  }

  const awsTeamTag = getAnnotation(entity, TEAM_METADATA_ANNOTATIONS.awsTeamTag);
  if (awsTeamTag) {
    rows.push({ label: 'AWS team tag (mnpg:team)', value: awsTeamTag });
  }

  const slackTeamId = getAnnotation(
    entity,
    TEAM_METADATA_ANNOTATIONS.slackTeamId,
  );
  if (slackTeamId) {
    rows.push({ label: 'Slack user group ID', value: slackTeamId });
  }

  const slackCommsId = getAnnotation(
    entity,
    TEAM_METADATA_ANNOTATIONS.slackChannelCommsId,
  );
  const slackCommsName = getAnnotation(
    entity,
    TEAM_METADATA_ANNOTATIONS.slackChannelCommsName,
  );
  if (slackCommsId) {
    rows.push({
      label: 'Slack comms channel',
      value: slackCommsName ?? slackCommsId,
      href: `${SLACK_WORKSPACE_BASE_URL}/${slackCommsId}`,
    });
  }

  const slackAlertsId = getAnnotation(
    entity,
    TEAM_METADATA_ANNOTATIONS.slackChannelAlertsId,
  );
  const slackAlertsName = getAnnotation(
    entity,
    TEAM_METADATA_ANNOTATIONS.slackChannelAlertsName,
  );
  if (slackAlertsId) {
    rows.push({
      label: 'Slack alerts channel',
      value: slackAlertsName ?? slackAlertsId,
      href: `${SLACK_WORKSPACE_BASE_URL}/${slackAlertsId}`,
    });
  }

  return rows;
};

export const hasTeamMetadata = (entity: Entity): boolean =>
  buildTeamMetadataRows(entity).length > 0;

type TeamMetadataCardProps = {
  rows: TeamMetadataRow[];
};

const TeamMetadataCardContent = ({ rows }: TeamMetadataCardProps) => (
  <InfoCard title="Team metadata">
    <Table size="small">
      <TableBody>
        {rows.map(row => (
          <TableRow key={row.label}>
            <TableCell>
              <Typography variant="body2" color="textSecondary">
                {row.label}
              </Typography>
            </TableCell>
            <TableCell>
              {row.href ? (
                <Link href={row.href} target="_blank" rel="noopener noreferrer">
                  {row.value}
                </Link>
              ) : (
                <Typography variant="body2">{row.value}</Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </InfoCard>
);

type TeamMetadataCardGridItemProps = {
  entity: Entity;
};

export const TeamMetadataCardGridItem = ({
  entity,
}: TeamMetadataCardGridItemProps) => {
  const rows = buildTeamMetadataRows(entity);
  if (rows.length === 0) {
    return null;
  }

  return (
    <Grid item xs={12} md={6}>
      <TeamMetadataCardContent rows={rows} />
    </Grid>
  );
};

export const TeamMetadataCard = ({ entity }: { entity: Entity }) => {
  const rows = buildTeamMetadataRows(entity);
  if (rows.length === 0) {
    return null;
  }

  return <TeamMetadataCardContent rows={rows} />;
};
